import maxBy from "lodash/maxBy";
import isNil from "lodash/isNil";
import includes from "lodash/includes";
import isString from "lodash/isString";

import isAfter from "date-fns/isAfter";
import {
  Product,
  Bundle,
  PaymentToken,
  ClientRequirement,
} from "@prisma/client";

export type BundleAmounts = {
  pendingAmount: number;
  totalAmount: number;
  soldAmount: number;
};

import { STATUS_APPROVED, STATUS_PENDING } from "enums/status";
import {
  BSC_TOKENS,
  PAYMENT_FIATS,
  PAYMENT_TOKENS,
  POLYGON_TOKENS,
} from "enums/paymentTokens";
import { CHAIN_IDS } from "enums/chainIds";
import { TOKEN_STANDARDS } from "enums/tokenStandards";
import { REQUIREMENTS_TYPES } from "enums/requirementsTypes";
import { STORE_VERSIONS } from "enums/storeTypes";

import JsonRequirement from "services/store/requirements/JsonRequirement.interface";
import JsonConstrainInterface from "services/store/constraints/JsonConstrain.interface";
import ConstrainCreator from "services/store/ConstrainCreator";
import { PaymentDetail } from "services/store/PaymentDetail.interface";

export const getProductReservedTime = (): Date => {
  const date = new Date();
  date.setMinutes(date.getMinutes() - 7);

  return date;
};

export const getMaxAmount = (
  jsonConstrains: JsonConstrainInterface[] | null,
  availableAmount: number,
): number => {
  const constrains = ConstrainCreator.fromJSON(jsonConstrains);
  const maxValueConstrain = maxBy(constrains, (constrain) =>
    constrain.getMaxValue(),
  );

  if (maxValueConstrain) {
    return maxValueConstrain.getMaxValue() > availableAmount &&
      availableAmount !== 0
      ? availableAmount
      : maxValueConstrain.getMaxValue() || 0;
  }

  return availableAmount;
};

export const generateTokenIds = (from: number, amount: number): number[] => {
  const start = from;
  const end = from + amount;

  return Array(end - start)
    .fill(0)
    .map((_, idx) => start + idx);
};

export const getAvailableAndTotalAmount = (
  bundle: Pick<Bundle, "maxAmount"> & BundleAmounts,
): { availableAmount: number; totalAmount: number } => {
  const {
    totalAmount: bundleTotalAmount,
    soldAmount = 0,
    pendingAmount = 0,
    maxAmount,
  } = bundle;

  const availableAmount =
    (isNil(maxAmount) ? bundleTotalAmount : maxAmount) -
    (soldAmount + pendingAmount);
  const totalAmount = isNil(maxAmount) ? bundleTotalAmount : maxAmount;

  return {
    availableAmount: availableAmount < 0 ? 0 : availableAmount,
    totalAmount,
  };
};

export const filterPaymentDetailsByChainId = (
  paymentDetails: PaymentDetail[],
  chainId: CHAIN_IDS,
): PaymentDetail[] => {
  const getChainTokens = (chainId) => {
    switch (chainId) {
      case CHAIN_IDS.POLYGON_MAINNET:
      case CHAIN_IDS.POLYGON_TESTNET:
        return POLYGON_TOKENS;
      case CHAIN_IDS.BSC:
      case CHAIN_IDS.BSC_TESTNET:
        return BSC_TOKENS;
      default:
        return PAYMENT_TOKENS;
    }
  };
  const allowedTokens = Object.values(getChainTokens(chainId));

  return paymentDetails.filter((paymentDetail) =>
    includes(allowedTokens, paymentDetail.paymentToken),
  );
};

export const getSubdomain = (hostname: string): string => {
  let host = "";
  const appUrl = new URL(process.env.NEXT_PUBLIC_APP_URL ?? "");
  const rootDomain = appUrl.host;
  if (hostname && rootDomain !== hostname) {
    host = hostname.replace(`.${rootDomain}`, "");
  }

  return host;
};

export const getLink = (value: string): string =>
  `${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/${value}`;

export const mapRequirements = (
  storeRequirements: ClientRequirement[],
): JsonRequirement[] =>
  /* eslint-disable-next-line */
  // @ts-ignore
  storeRequirements.map((requirement) => {
    switch (requirement.type) {
      case REQUIREMENTS_TYPES.NFT:
        return {
          type: requirement.type,
          description: requirement.description,
          nftValue: requirement.nftValue,
          tokenStandard: requirement.tokenStandard as TOKEN_STANDARDS,
          tokenAddresses: requirement.tokenAddresses as string[],
          tokenIds: requirement.tokenIds as string[],
          chainId: requirement.chainId as CHAIN_IDS,
        };
      case REQUIREMENTS_TYPES.TOKEN:
        return {
          type: requirement.type,
          description: requirement.description,
          tokenValue:
            requirement.tokenValue &&
            parseFloat(requirement.tokenValue.toString()),
          chainId: requirement.chainId as CHAIN_IDS,
          tokenAddresses: requirement.tokenAddresses as string[],
        };
      case REQUIREMENTS_TYPES.WHITELIST:
        return {
          type: requirement.type,
          description: requirement.description,
          purpose: requirement.purpose,
          tokenAddresses: requirement.tokenAddresses as string[],
        };
    }
  });

export const recalculatePaymentDetails = (
  paymentTokens: (PAYMENT_TOKENS | BSC_TOKENS | POLYGON_TOKENS)[],
  refPrice: number | string,
  customTokens: PaymentToken[],
): PaymentDetail[] => {
  const refChainTokens: CHAIN_IDS[] = [];

  return paymentTokens.map((token) => {
    const price = 100;
    let additional = {};
    const chainToken = customTokens.find(
      (customToken) => customToken.name === token,
    );
    if (chainToken && !includes(refChainTokens, chainToken.chainId)) {
      additional = {
        refPrice: isString(refPrice) ? parseFloat(refPrice) : refPrice,
        refToken: PAYMENT_FIATS.USD,
      };
      refChainTokens.push(chainToken.chainId);
    }
    return {
      paymentToken: token,
      price,
      ...additional,
    };
  });
};

export const mapProductAmounts = (
  storeProducts: Pick<Product, "status" | "deletedAt" | "updatedAt">[],
): {
  pendingAmount: number;
  totalAmount: number;
  soldAmount: number;
} => {
  const date = getProductReservedTime();

  return storeProducts.reduce(
    (acc, storeProduct) => {
      acc.totalAmount = ++acc.totalAmount;
      acc.pendingAmount =
        storeProduct.status === STATUS_PENDING &&
        storeProduct.deletedAt === null &&
        storeProduct.updatedAt &&
        isAfter(new Date(storeProduct.updatedAt), date)
          ? ++acc.pendingAmount
          : acc.pendingAmount;
      acc.soldAmount =
        storeProduct.status === STATUS_APPROVED &&
        storeProduct.deletedAt === null
          ? ++acc.soldAmount
          : acc.soldAmount;
      return acc;
    },
    {
      pendingAmount: 0,
      totalAmount: 0,
      soldAmount: 0,
    },
  );
};

export const getIfStore = (storeType: STORE_VERSIONS) => {
  return [STORE_VERSIONS.UNIQLY, STORE_VERSIONS.PARTNER].includes(storeType);
};

export const MAX_CUSTOM_ATTRIBUTES = 10;
